import { useMemo } from 'react';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useDropdownsContext } from '@contexts/dropdowns';
import { SchedulerSidebarEquipmentMember } from '@contexts/scheduler/sidebar/types';
import { getConflicts } from '../multiDay/helpers';
import { PhoneResourcesProps } from '../crew/types';
import { assetLabel } from './helpers';

export const useEquipmentOptions = ({
  multi,
  specialty,
}: Pick<PhoneResourcesProps, 'multi' | 'specialty'>) => {
  const { equipmentMembers, setState, conflictsByAssetId } = useSchedulerSidebar();
  const {
    assetMembers: { options: assetOptions, disabled },
  } = useDropdownsContext();

  const multiConflicts = (id: string, name: string) => {
    if (!multi) return {};
    const hits = getConflicts(multi.lookup, 'assets', id);
    return {
      conflictDays: new Set(hits.map((hit) => hit.date)).size,
      onShowConflicts: () => multi.onShowConflicts(name, hits),
    };
  };

  const assets = useMemo(() => assetOptions.filter((el) => el.id !== 'unassigned'), [assetOptions]);
  const options = assets.map((el) => ({
    value: el.id,
    label: assetLabel(el.name, el.internalId),
    conflicts: multi ? [] : conflictsByAssetId[el.id] || [],
    qualified: !specialty || Boolean(el.specialtyValues?.includes(specialty)),
    ...multiConflicts(el.id, assetLabel(el.name, el.internalId)),
  }));
  const selected = equipmentMembers
    .filter((el) => el.assetId && !el.discardedAt)
    .map((el) => el.assetId);

  const toMember = (assetId: string): SchedulerSidebarEquipmentMember => {
    const asset = assets.find((el) => el.id === assetId);
    return {
      id: undefined,
      assetId,
      name: asset?.name || '',
      internalId: asset?.internalId || '',
      conflicts: [],
      edit: false,
    };
  };

  const toggle = (assetId: string) =>
    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: selected.includes(assetId)
        ? prev.equipmentMembers.filter((el) => el.assetId !== assetId)
        : [...prev.equipmentMembers, toMember(assetId)],
    }));

  const replace = (fromId: string, toId: string) => {
    if (toId === fromId || selected.includes(toId)) return;
    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: prev.equipmentMembers.map((el) =>
        el.assetId === fromId ? toMember(toId) : el,
      ),
    }));
  };

  return { options, selected, toggle, replace, multiConflicts, disabled };
};
